import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import { PulseAIImage, usePulseAIActive } from "../PulseAIImage";
import { Upload, Loader2, Check, Trash2, Crown, Sparkles, X } from "lucide-react";

const CustomBrandPanel = () => {
  const { user } = useAuth();
  const [brand, setBrand] = useState({
    enabled: false,
    plan: "",
    custom_name: "",
    custom_suffix: " Engine™",
    image_url: null,
    has_custom_image: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);
  const viewer = usePulseAIActive();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pulseai/custom-brand/my`);
      setBrand(res.data);
    } catch (e) {
      console.error("Brand load error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showMsg = (text, ok = true) => {
    setMessage({ text, ok });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      showMsg("Súbor je väčší ako 4 MB", false);
      return;
    }
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      await axios.post(`${API}/pulseai/custom-brand/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      showMsg("Obrázok úspešne nahratý");
    } catch (e) {
      showMsg(e.response?.data?.detail || "Nahratie zlyhalo", false);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pulseai/custom-brand/settings`, {
        custom_name: brand.custom_name,
        custom_suffix: brand.custom_suffix,
      });
      await load();
      showMsg("Nastavenia uložené");
    } catch (e) {
      showMsg(e.response?.data?.detail || "Uloženie zlyhalo", false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Naozaj chceš vymazať custom brand a vrátiť sa k default PulseAI?"))
      return;
    try {
      await axios.delete(`${API}/pulseai/custom-brand`);
      await load();
      showMsg("Custom brand vymazaný");
    } catch (e) {
      showMsg("Vymazanie zlyhalo", false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
      </div>
    );
  }

  // Enterprise plan gate
  if (!brand.enabled) {
    return (
      <div className="max-w-3xl" data-testid="custom-brand-locked">
        <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-2 border-amber-300 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-4">
            <Crown className="w-8 h-8 text-amber-600" />
            <h2 className="text-2xl font-black text-gray-900">
              Brand your own PulseAI™
            </h2>
            <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold uppercase rounded-full tracking-wider">
              Enterprise
            </span>
          </div>
          <p className="text-gray-700 mb-6 leading-relaxed">
            White-label funkcia dostupná iba pre <strong>Enterprise plán</strong>.
            Nahraj vlastný avatar a premenuj „PulseAI Engine™" na svoj vlastný
            brand (napr. "<em>MyBrand AI™</em>"). Tvoj AI bude vyzerať tak, ako
            ho chceš ty — v hero, showcase aj vo všetkých AI výstupoch.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <Upload className="w-5 h-5 text-amber-600 mb-2" />
              <p className="font-bold text-sm text-gray-900 mb-1">
                Vlastný avatar
              </p>
              <p className="text-xs text-gray-500">
                PNG/JPG/WEBP do 4 MB. Automaticky sa zobrazí všade v aplikácii.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <Sparkles className="w-5 h-5 text-amber-600 mb-2" />
              <p className="font-bold text-sm text-gray-900 mb-1">
                Premenovať AI
              </p>
              <p className="text-xs text-gray-500">
                "PulseAI Engine™" → "MyBrand AI™", "SmartBot Pro™", čokoľvek.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <Crown className="w-5 h-5 text-amber-600 mb-2" />
              <p className="font-bold text-sm text-gray-900 mb-1">
                Agency ready
              </p>
              <p className="text-xs text-gray-500">
                Ideálne pre agentúry — tvoje logo, tvoja identita, tvoj produkt.
              </p>
            </div>
          </div>

          <a
            href="/#pricing"
            data-testid="upgrade-enterprise-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-bold rounded-lg hover:bg-purple-600 transition-colors"
          >
            <Crown className="w-4 h-4" />
            Upgrade na Enterprise
          </a>
          <p className="text-xs text-gray-500 mt-3">
            Aktuálny plán:{" "}
            <span className="font-mono uppercase bg-gray-100 px-2 py-0.5 rounded">
              {brand.plan || "free"}
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6" data-testid="custom-brand-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-cyan-900 rounded-xl p-6 border border-purple-500/30">
        <div className="flex items-center gap-3 mb-2">
          <Crown className="w-6 h-6 text-amber-300" />
          <h2 className="text-xl font-bold text-white">Brand your own PulseAI™</h2>
          <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold uppercase rounded-full tracking-wider">
            Enterprise
          </span>
        </div>
        <p className="text-purple-200 text-sm">
          Prepíš default PulseAI Engine™ svojím vlastným brandingom — avatar +
          meno. Zmeny sa aplikujú okamžite v celej aplikácii (hero, showcase, AI
          výstupy). Viditeľné iba tebe, keď si prihlásený.
        </p>
      </div>

      {message && (
        <div
          data-testid="brand-message"
          className={`p-3 rounded-lg text-sm font-medium ${
            message.ok
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Image upload */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-4 h-4 text-purple-600" />
            Custom Avatar
          </h3>

          {/* Current / Preview */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 ring-2 ring-purple-300 overflow-hidden">
              {preview ? (
                <img
                  src={preview}
                  alt="preview"
                  className="w-full h-full object-cover"
                  data-testid="brand-preview-img"
                />
              ) : brand.has_custom_image ? (
                <img
                  src={`${API}${(brand.image_url || "").replace(/^\/api/, "")}?t=${Date.now()}`}
                  alt="custom brand"
                  className="w-full h-full object-cover"
                  data-testid="brand-current-img"
                />
              ) : (
                <PulseAIImage variantKey={viewer.key} size={96} />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-900 font-bold">
                {preview
                  ? "Náhľad nového obrázka"
                  : brand.has_custom_image
                  ? "Tvoj aktuálny brand"
                  : "Default PulseAI™"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Odporúčané: štvorec, 512×512+ px, .png s transparentným pozadím
              </p>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileChange}
            className="hidden"
            data-testid="brand-file-input"
          />
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="brand-pick-file-btn"
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {preview ? "Zmeniť súbor" : "Vybrať súbor"}
            </button>
            {preview && (
              <>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  data-testid="brand-upload-btn"
                  className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-2"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Nahrať
                </button>
                <button
                  onClick={() => {
                    setPreview(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {brand.has_custom_image && !preview && (
            <button
              onClick={handleDelete}
              data-testid="brand-delete-btn"
              className="w-full mt-3 px-4 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Vymazať custom avatar
            </button>
          )}

          <p className="text-[11px] text-gray-400 mt-3">
            Max. veľkosť: 4 MB · formáty: PNG, JPG, WEBP
          </p>
        </div>

        {/* Right: Name/suffix */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            Názov tvojej AI
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                Hlavný názov
              </label>
              <input
                type="text"
                value={brand.custom_name}
                onChange={(e) =>
                  setBrand({ ...brand, custom_name: e.target.value })
                }
                placeholder="napr. MyBrand, SmartBot, NovaAI"
                maxLength={30}
                data-testid="brand-name-input"
                className="w-full px-4 py-2.5 border border-gray-300 focus:border-purple-500 focus:outline-none rounded-lg text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Nahradí "PulseAI" v celej aplikácii. Max 30 znakov.
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                Suffix / TM
              </label>
              <input
                type="text"
                value={brand.custom_suffix}
                onChange={(e) =>
                  setBrand({ ...brand, custom_suffix: e.target.value })
                }
                placeholder=" Engine™"
                maxLength={20}
                data-testid="brand-suffix-input"
                className="w-full px-4 py-2.5 border border-gray-300 focus:border-purple-500 focus:outline-none rounded-lg text-sm font-mono"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Default: " Engine™". Nastav napr. " AI™", " Pro™" alebo prázdne.
              </p>
            </div>

            {/* Live preview */}
            <div className="p-4 bg-gradient-to-r from-purple-900/5 to-cyan-900/5 border border-purple-200 rounded-lg">
              <p className="text-[10px] text-gray-500 uppercase font-mono tracking-wider mb-2">
                Live preview
              </p>
              <div className="inline-flex items-center gap-2.5 pl-1 pr-4 py-1 bg-gradient-to-r from-purple-900/90 via-indigo-900/90 to-cyan-900/90 border border-purple-500/40 rounded-full">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-black ring-1 ring-cyan-400/50">
                  {preview ? (
                    <img
                      src={preview}
                      className="w-full h-full object-cover"
                      alt=""
                    />
                  ) : brand.has_custom_image ? (
                    <img
                      src={`${API}${(brand.image_url || "").replace(/^\/api/, "")}?t=${Date.now()}`}
                      className="w-full h-full object-cover"
                      alt=""
                    />
                  ) : (
                    <PulseAIImage variantKey={viewer.key} size={32} />
                  )}
                </div>
                <span className="text-[11px] font-mono uppercase tracking-widest text-cyan-300">
                  Powered by{" "}
                  <span className="text-white font-bold">
                    {(brand.custom_name || "PulseAI") +
                      (brand.custom_suffix || " Engine™")}
                  </span>
                </span>
              </div>
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              data-testid="brand-save-settings-btn"
              className="w-full px-4 py-2.5 bg-gray-900 hover:bg-purple-600 text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Uložiť nastavenia
            </button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="font-semibold mb-1">💡 Kde sa tvoj brand zobrazí</p>
        <ul className="text-xs space-y-1 list-disc list-inside">
          <li>Hero badge na landing page ("Powered by {(brand.custom_name || "PulseAI") + (brand.custom_suffix || " Engine™")}")</li>
          <li>Showcase sekcia ("Meet {(brand.custom_name || "PulseAI") + (brand.custom_suffix || " Engine™")}")</li>
          <li>Vo všetkých AI výstupoch (Brand Monitor, Keyword Research, SEO Score)</li>
          <li>Viditeľné iba tebe, keď si prihlásený. Ostatní používatelia vidia default.</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomBrandPanel;
